import { useCallback, useState } from 'react';

import { clearAuthData, extractEmailDomain, isValidEmail, storeAuthContext } from '@/auth/authUtils';
import { useOidcAuth } from '@/auth/useOidcAuth';
import { useSamlAuth } from '@/auth/useSamlAuth';
import { controlPlaneService } from '@/services/controlPlane.service';

const getErrorMessage = (err: unknown, fallback: string) => {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message?: unknown }).message ?? fallback);
  }
  return fallback;
};

/**
 * Custom hook for authentication operations.
 *
 * Provides methods for login, logout, and OAuth callback handling.
 */
export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { handleOAuthCallback: handleOidcCallback, startOidcLogin } = useOidcAuth();
  const { completeSamlLogin: completeSamlSession, startSamlLogin } = useSamlAuth();

  const runAuthAction = useCallback(async function runAuthAction<T>(
    action: () => Promise<T>,
    fallbackErrorMessage: string,
  ): Promise<T> {
    setLoading(true);
    setError(null);

    try {
      return await action();
    } catch (err) {
      const message = getErrorMessage(err, fallbackErrorMessage);
      setError(message);
      setLoading(false);
      throw err;
    }
  }, []);

  const loginWithEmail = useCallback(
    async (email: string) => {
      setError(null);
      setLoading(true);

      try {
        if (!isValidEmail(email)) {
          throw new Error('Please enter a valid email address');
        }

        const emailDomain = extractEmailDomain(email);
        const authConfig = await controlPlaneService.getAuthByEmailDomain(emailDomain, false);

        storeAuthContext(email, emailDomain, authConfig);

        if (authConfig.protocol === 'saml') {
          startSamlLogin(authConfig);
          return;
        }

        // Default is OIDC login flow
        startOidcLogin(authConfig, email);
      } catch (err) {
        const message = getErrorMessage(err, 'Failed to initiate login. Please try again.');
        setError(message);
        setLoading(false);
        throw err;
      }
    },
    [startOidcLogin, startSamlLogin],
  );

  const handleOAuthCallback = useCallback(
    (code: string, state: string) =>
      runAuthAction(() => handleOidcCallback(code, state), 'Authentication failed. Please try again.'),
    [handleOidcCallback, runAuthAction],
  );

  const completeSamlLogin = useCallback(
    () => runAuthAction(completeSamlSession, 'Authentication failed. Please try again.'),
    [completeSamlSession, runAuthAction],
  );

  const logout = useCallback(() => {
    clearAuthData();
    window.location.href = '/login';
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    loginWithEmail,
    handleOAuthCallback,
    completeSamlLogin,
    logout,
    clearError,
  };
};
